import { subYears } from 'date-fns';
import * as Yup from 'yup';

export const signupSchema = Yup.object().shape({
  studentFirstName: Yup.string().required('Student first name is required'),
  parentsEmail: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  confirmParentsEmail: Yup.string()
    .oneOf([Yup.ref('parentsEmail')], 'Email addresses must match')
    .required('Please confirm your email'),
  studentsAge: Yup.string().required('Age is required'),
  studentsGender: Yup.string().required('Gender is required'),
  subscriptionType: Yup.string().required('Subscription type is required'),
  couponCode: Yup.string(),
  promoCode: Yup.string(),
  referralSource: Yup.string().required(
    'Please tell us how you heard about us',
  ),
  otherReferralSource: Yup.string().when('referralSource', {
    is: 'other',
    then: (schema) => schema.required('Please specify how you heard about us'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

export const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
});

export const resetPasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .min(6, 'New password must be at least 6 characters long')
    .required('New password is required')
    .notOneOf(
      [Yup.ref('oldPassword')],
      'New password cannot be the same as the old password',
    ),
});

export const resetEmailSchema = (currentEmail: string) =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .email('Invalid email format')
      .required('Email is required')
      .notOneOf(
        [currentEmail],
        'The new email must be different from the current email',
      ),
    confirmEmail: Yup.string()
      .trim()
      .oneOf([Yup.ref('email')], 'Email addresses must match')
      .required('Confirm Email is required'),
  });

export const affiliateSchema = Yup.object()
  .shape({
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    country: Yup.string().required('Country is required'),
    state: Yup.string().required('State is required'),
    city: Yup.string().required('City is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    gender: Yup.string().required('Gender is required'),
    age: Yup.string().required('Age is required'),
    // postal_code: Yup.string().required('Postal Code is required'),
    address: Yup.string().required('Address is required'),
    dob: Yup.date()
      .required('Date of Birth is required')
      .test(
        'is-at-least-10-years-old',
        'DOB should be at least 10 years old',
        (value) => value && value <= subYears(new Date(), 10),
      ),
    expertise: Yup.array()
      .min(1, 'At least one area of expertise is required')
      .required(),
    // platforms: Yup.array()
    //   .min(1, 'At least one promotion platform is required')
    //   .required(),
    website_url: Yup.string()
      .test(
        'is-valid-url',
        'Website URL must start with "http://" or "https://"',
        (value) => !value || /^(http:\/\/|https:\/\/)/.test(value),
      )
      .nullable()
      .notRequired(),

    linked_in: Yup.string()
      .test(
        'is-valid-linkedin',
        'LinkedIn link must start with "https://www.linkedin.com/"',
        (value) =>
          !value || /^https?:\/\/(www\.)?linkedin\.com\/.+/.test(value),
      )
      .nullable()
      .notRequired(),
    facebook: Yup.string()
      .test(
        'is-valid-facebook',
        'Facebook link must start with "https://www.facebook.com/"',
        (value) =>
          !value || /^https?:\/\/(www\.)?facebook\.com(\/.*)?$/.test(value),
        // (value) =>
        //   !value || /^https?:\/\/(www\.)?facebook\.com\/.+/.test(value),
      )
      .nullable()
      .notRequired(),

    instagram: Yup.string()
      .test(
        'is-valid-instagram',
        'Instagram link must start with "https://www.instagram.com/"',
        (value) =>
          !value || /^https?:\/\/(www\.)?instagram\.com\/.+/.test(value),
      )
      .nullable()
      .notRequired(),

    twitter: Yup.string()
      .test(
        'is-valid-twitter',
        'Twitter link must start with "https://twitter.com/" or "https://x.com/"',
        (value) =>
          !value || /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/.+/.test(value),
      )
      .nullable()
      .notRequired(),

    youtube: Yup.string()
      .test(
        'is-valid-youtube',
        'YouTube link must start with "https://www.youtube.com/"',
        (value) => !value || /^https?:\/\/(www\.)?youtube\.com\/.+/.test(value),
      )
      .nullable()
      .notRequired(),
    tiktok: Yup.string()
      .test(
        'is-valid-tiktok',
        'TikTok link must start with "https://www.tiktok.com/"',
        (value) => !value || /^https?:\/\/(www\.)?tiktok\.com\/.+/.test(value),
      )
      .nullable()
      .notRequired(),

    audience_size: Yup.number().nullable(),
    referral_source: Yup.string(),
  })
  .test(
    'at-least-one-link',
    'Please provide at least one valid link (Website, LinkedIn, Facebook, Instagram, Twitter, YouTube, or TikTok)',
    (values) =>
      !!(
        values.website_url ||
        values.linked_in ||
        values.facebook ||
        values.instagram ||
        values.twitter ||
        values.youtube ||
        values.tiktok
      ),
  );
